// ============================================================================
// GtoQuickButton - the floating "Charts" pill on every training screen
// ----------------------------------------------------------------------------
// Mounted once in App. Shows on /training routes (not the charts page
// itself), sits above the bottom navigation, and opens the preflop chart
// in a drawer preset to whatever seat and stack the current screen has
// published through gtoContext. A player mid-hand can peek at the chart
// for their exact spot without leaving the table.
// ============================================================================
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Button, Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
  useDisclosure, useBreakpointValue, Text, HStack,
} from '@chakra-ui/react';
import { Grid3x3 } from 'lucide-react';
import GtoChart from './GtoChart';
import { useGtoContext } from '../../services/training/gtoContext';

const GtoQuickButton: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const ctx = useGtoContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const placement = useBreakpointValue<'bottom' | 'right'>({ base: 'bottom', md: 'right' }) || 'bottom';

  const visible = useMemo(() => {
    const p = location.pathname;
    return p.startsWith('/training') && !p.startsWith('/training/charts') && !p.startsWith('/training/report') && !p.startsWith('/training/coach');
  }, [location.pathname]);
  if (!visible) return null;

  const hint = ctx.label || (ctx.position ? `${ctx.position}${ctx.stackBB ? ` · ${Math.round(ctx.stackBB)} BB` : ''}` : '');
  const subtitle = !ctx.position
    ? 'Pick your seat to see what to play'
    : ctx.stage === 'postflop'
      ? 'The flop is out. Preflop charts cover the first decision only; this one is for the next hand.'
      : `${ctx.actionNote ? `${ctx.actionNote}. ` : ''}Set to your seat: ${ctx.position}${ctx.stackBB ? `, about ${Math.round(ctx.stackBB)} big blinds` : ''}${ctx.hand ? '. Your hand is ringed in gold' : ''}.`;

  return (
    <>
      <Box position="fixed" right={{ base: '12px', md: '20px' }} bottom={{ base: 'calc(env(safe-area-inset-bottom) + 84px)', md: '24px' }} zIndex={1400}>
        <Button onClick={onOpen} minH="48px" px={4} borderRadius="full" bg="#0f1722" color="#22d3ee"
          border="1px solid rgba(34,211,238,0.6)" boxShadow="0 8px 24px rgba(0,0,0,0.45)" _hover={{ bg: '#141c2b' }}
          leftIcon={<Grid3x3 size={18} />} fontWeight="800" fontSize="md">
          Charts{hint ? <Text as="span" color="gray.400" fontWeight="600" fontSize="sm" ml={2}>{hint}</Text> : null}
        </Button>
      </Box>
      <Drawer isOpen={isOpen} onClose={onClose} placement={placement} size={placement === 'bottom' ? 'full' : 'md'}>
        <DrawerOverlay bg="blackAlpha.700" />
        <DrawerContent bg="#0a1420" color="white" borderTopRadius={placement === 'bottom' ? '20px' : 0} maxH={placement === 'bottom' ? '92vh' : undefined}>
          <DrawerCloseButton mt={1} />
          <DrawerHeader pb={2}>
            <HStack justify="space-between" pr={8}>
              <Box>
                <Text fontSize="lg" fontWeight="800">Preflop chart</Text>
                <Text fontSize="sm" color={ctx.stage === 'postflop' ? '#fde68a' : 'gray.400'} fontWeight="500">{subtitle}</Text>
              </Box>
              <Button size="sm" variant="ghost" color="#22d3ee" onClick={() => { onClose(); navigate('/training/charts'); }}>Full page</Button>
            </HStack>
          </DrawerHeader>
          <DrawerBody pb={8}>
            <GtoChart situation={ctx.situation} position={ctx.position} stackBB={ctx.stackBB} hand={ctx.hand} spot={ctx.spot} compact />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default GtoQuickButton;
